<template>
    <div class="file-upload">
        <img
            v-if="preview"
            :alt="preview.alt"
            :src="preview.src"
            class="preview"
            loading="lazy"
        />

        <Icon
            v-if="preview && removable"
            icon="close-bold"
            class="button-remove"
            @click.prevent.stop="removeFile"
        />

        <div
            v-if="singleFile ? !preview : true"
            ref="dropZoneRef"
            class="file-upload-area"
            :class="{ success: isOverDropZone }"
            @click="open()"
        >
            {{ uploadLabel }}

            <TransitionFade>
                <LoadingAnimation
                    v-if="fetching"
                    class="loading-animation"
                />
            </TransitionFade>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useDropZone, useFileDialog } from '@vueuse/core'

import Icon from './icon/Icon.vue'
import LoadingAnimation from './LoadingAnimation.vue'
import TransitionFade from '../transitions/TransitionFade.vue'

type Preview = {
    src: string
    alt: string
}

const { removable = true, singleFile } = defineProps<{
    removable?: boolean
    fetching?: boolean
    singleFile?: boolean
}>()

const emit = defineEmits<{
    'file-selected': [file: File | null]
}>()

const file = ref<File | null>(null)

const preview = computed<Preview | null>(
    () =>
        file.value && {
            src: URL.createObjectURL(file.value),
            alt: file.value.name,
        }
)

const fileChanged = (theFile: File | null): void => {
    file.value = theFile

    emit('file-selected', theFile)

    if (!theFile) {
        reset()
    }
}

const removeFile = () => fileChanged(null)

const dropZoneRef = useTemplateRef('dropZoneRef')

const onDrop = (files: FileList | File[] | null) => {
    const theFile = files?.[0]

    if (theFile) {
        fileChanged(theFile)
    }
}

const { isOverDropZone } = useDropZone(dropZoneRef, {
    onDrop,
    dataTypes: (types) => {
        return !types.some((type) => !type.startsWith('image/'))
    },
    multiple: false,
    preventDefaultForUnhandled: true,
})

const { onChange, open, reset } = useFileDialog({
    accept: 'image/*',
    directory: false,
})

onChange(onDrop)

const uploadLabel = computed(() =>
    singleFile
        ? 'Drop an image here, or click to upload.'
        : 'Drop images here, or click to upload.'
)
</script>

<style lang="scss" scoped>
.file-upload {
    position: relative;
    width: 100%;
    max-width: 600px;

    display: flex;
    align-items: center;
    gap: 20px;
    justify-content: center;

    --height: 200px;
    --border-radius: 24px;
    --border-radius-squircle: 64px;

    .preview {
        aspect-ratio: 1/1;
        width: var(--height);
        object-fit: cover;

        border: $border-grey;
        border-radius: var(--border-radius);
        box-shadow: 0 0 10px rgb($black, 0.2);

        @supports (corner-shape: squircle) {
            border-radius: var(--border-radius-squircle);
            // stylelint-disable-next-line
            corner-shape: squircle;
        }
    }

    .button-remove {
        position: absolute;
        top: 0px;
        left: calc(var(--height) - 16px);
        color: $white;
        border-radius: 50%;
        width: 28px;
        height: 28px;
        border: 1px solid $white;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $slate;
        box-shadow: 0 0 4px rgb($black, 0.4);
        cursor: pointer;
    }

    .file-upload-area {
        position: relative;
        width: 100%;
        height: var(--height);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px dashed #ccc;
        border-radius: var(--border-radius);

        padding: 16px;

        font-style: normal;

        color: $slate;
        font-family: $effra-font-stack;
        font-size: 1rem;
        line-height: 30px;
        font-weight: 400;

        cursor: pointer;

        @supports (corner-shape: squircle) {
            border-radius: var(--border-radius-squircle);
            // stylelint-disable-next-line
            corner-shape: squircle;
        }

        &.success {
            border-color: green;
        }

        .loading-animation {
            position: absolute;
            background-color: $white;
            width: 95%;
        }
    }
}
</style>
